/**
 * Staff Forms Page — GHL iframe page.
 *
 * Four-tab layout:
 *   Tab 1: Submissions — inbox of submitted forms
 *   Tab 2: Form Library — manage form schemas
 *   Tab 3: Packages — bundle forms into one patient link
 *   Tab 4: Send Form — assign a form to a contact
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../lib/auth-context.js';
import SubmissionsInbox from './SubmissionsInbox.js';
import SubmissionReview from './SubmissionReview.js';
import FormLibrary from './FormLibrary.js';
import FormBuilderPage from './FormBuilderPage.js';
import PackageLibrary from './PackageLibrary.js';
import PackageBuilder from './PackageBuilder.js';
import SendFormPanel from './SendFormPanel.js';
import { SubmissionSummary, FormSchema, PackageSummary } from '../../../lib/api.js';

type Tab = 'submissions' | 'library' | 'packages' | 'send';

export default function FormsPage(): React.ReactElement {
  const { loading, error: authError, isStaff } = useAuth();
  const [tab,              setTab]          = useState<Tab>('submissions');
  const [selectedSub,      setSelectedSub]  = useState<SubmissionSummary | null>(null);
  const [editingSchema,    setEditing]      = useState<FormSchema | null | undefined>(null);
  const [buildingNew,      setBuildingNew]  = useState(false);
  const [editingPackage,   setEditingPkg]   = useState<PackageSummary | null | undefined>(null);
  const [buildingPackage,  setBuildingPkg]  = useState(false);
  const [refreshSignal,    setRefresh]      = useState(0);
  const [preselectContact, setPreContact]   = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams]     = useSearchParams();

  const handleSendMessage = useCallback((contactGhlId: string) => {
    setPreContact(contactGhlId);
    setTab('send');
  }, []);

  // Deep-link from the contact widget: /app/forms?contactId={id} opens the
  // Send Form tab with the contact pre-selected. Consume the param once so a
  // manual tab switch afterwards is not re-overridden, and strip it from the URL.
  useEffect(() => {
    const cid = searchParams.get('contactId');
    if (cid) {
      setPreContact(cid);
      setTab('send');
      searchParams.delete('contactId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (loading) {
    return <div style={styles.center}><p style={styles.muted}>Authenticating…</p></div>;
  }
  if (authError || !isStaff) {
    return <div style={styles.center}><p style={{ color: '#c0392b' }}>{authError ?? 'Not authorized'}</p></div>;
  }

  // Show form builder full-screen
  if (buildingNew || editingSchema !== null) {
    return (
      <FormBuilderPage
        editingSchema={editingSchema}
        onSaved={() => {
          setBuildingNew(false);
          setEditing(null);
          setTab('library');
          setRefresh((n) => n + 1);
        }}
        onCancel={() => { setBuildingNew(false); setEditing(null); }}
      />
    );
  }

  // Show package builder full-screen
  if (buildingPackage || editingPackage !== null) {
    return (
      <PackageBuilder
        editingPackage={editingPackage}
        onSaved={() => {
          setBuildingPkg(false);
          setEditingPkg(null);
          setTab('packages');
          setRefresh((n) => n + 1);
        }}
        onCancel={() => { setBuildingPkg(false); setEditingPkg(null); }}
      />
    );
  }

  return (
    <div style={styles.root}>
      {/* Topbar */}
      <div style={styles.topbar}>
        <div style={styles.topbarLeft}>
          <span style={{ fontSize: 18 }}>📋</span>
          <span style={styles.topbarTitle}>Forms</span>
        </div>
        <div style={styles.tabs}>
          {([
            ['submissions', 'Submissions'],
            ['library',     'Form Library'],
            ['packages',    'Packages'],
            ['send',        'Send Form'],
          ] as [Tab, string][]).map(([t, label]) => (
            <button
              key={t}
              style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
              onClick={() => setTab(t)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {tab === 'submissions' && (
          <div style={styles.splitPane}>
            <div style={styles.sidebarPane}>
              <SubmissionsInbox
                selectedId={selectedSub?.id ?? null}
                onSelect={setSelectedSub}
                refreshSignal={refreshSignal}
              />
            </div>
            <div style={styles.contentPane}>
              {selectedSub ? (
                <SubmissionReview
                  key={selectedSub.id}
                  submissionId={selectedSub.id}
                  onSendMessage={handleSendMessage}
                />
              ) : (
                <div style={styles.empty}>
                  <span style={{ fontSize: 40 }}>📝</span>
                  <p style={styles.muted}>Select a submission to review it</p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'library' && (
          <FormLibrary
            onEdit={(s) => setEditing(s)}
            onNewForm={() => { setEditing(undefined); setBuildingNew(true); }}
            refreshSignal={refreshSignal}
          />
        )}

        {tab === 'packages' && (
          <PackageLibrary
            onEdit={(p) => setEditingPkg(p)}
            onNewPackage={() => { setEditingPkg(undefined); setBuildingPkg(true); }}
            refreshSignal={refreshSignal}
          />
        )}

        {tab === 'send' && (
          <SendFormPanel
            preselectedContactId={preselectContact}
            onSent={() => setRefresh((n) => n + 1)}
          />
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex', flexDirection: 'column', height: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: '#f9fafb',
  },
  topbar: {
    display: 'flex', alignItems: 'center', padding: '0 20px',
    background: '#fff', borderBottom: '1px solid #e5e7eb',
    height: 52, flexShrink: 0, gap: 24,
  },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  topbarTitle: { fontWeight: 600, fontSize: 16, color: '#111827' },
  tabs: { display: 'flex' },
  tab: {
    padding: '0 16px', height: 52, border: 'none', background: 'none',
    cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#6b7280',
    borderBottom: '2px solid transparent',
  },
  tabActive: { color: '#2563eb', borderBottom: '2px solid #2563eb' },
  body:      { flex: 1, overflow: 'hidden' },
  splitPane: { display: 'flex', height: '100%', overflow: 'hidden' },
  sidebarPane: { width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  contentPane: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  muted:  { color: '#9ca3af', fontSize: 14 },
  empty:  { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 },
};
